import Keycloak from 'keycloak-js'
import Axios from './axios'
import config from './config'
import { isPublicRoute } from './services/auth'
import { getInitialLocale } from './i18n/locale'

const keycloak = new Keycloak(config.keycloakConfig);

export const updateToken = async () => {
  try {
    await keycloak.updateToken(100);
    const token = getAccessToken();
    if (!token) throw new Error("Invalid");
    Axios.setAuthorization(getAccessToken()!);
    return token;
  } catch (error) {
    console.log(`not refreshed :: ` + new Date());
    await forceLogin();
  }
};

keycloak.onTokenExpired = updateToken;

export const initialize = () => {
  // Survey is rendered within iframes in many cases. Prevents redirect to keycloak in those cases.
  const isSurveyRoute = window.location.pathname.startsWith('/customer/survey/form/');
  // Public pages (e.g. the emailed DLT form) must resolve without a session too.
  const skipLoginRedirect = isSurveyRoute || isPublicRoute();

  // Keycloak's session-status iframe is framed from auth.qa.casaretail.ai, whose `frame-ancestors`
  // CSP lists only the deployed dashboard origins — never localhost. Left on, the browser blocks
  // the frame and init resolves unauthenticated, so local dev lands on the error screen. Deployed
  // origins are in the allow-list and keep the session check.
  const isLocalOrigin = ['localhost', '127.0.0.1'].includes(window.location.hostname);

  return keycloak.init({
    onLoad: skipLoginRedirect ? 'check-sso' : 'login-required',
    checkLoginIframe: !isLocalOrigin,
    locale: getInitialLocale(),
  })
}

export const redirectToLogin = () => {
  keycloak.clearToken()
  keycloak.login({ locale: getInitialLocale() })
}

export const logout = async () => {
  // VAPT #15 (Concurrent User): api-server tracks one active session per user to enforce a
  // single-session limit, but has no way to observe a logout on its own — keycloak.logout()
  // below redirects straight to Keycloak's own end-session endpoint, api-server never sees it.
  // Without this call, logging back in shortly after would be wrongly rejected as "already
  // signed in elsewhere".
  //
  // Deliberately raw fetch with keepalive, not Axios: forceLogin() below fires this in parallel
  // with redirectToLogin(), which navigates the browser away almost immediately (keycloak.login()).
  // A normal axios/XHR request racing that navigation can get silently cancelled mid-flight —
  // keepalive is the browser mechanism that specifically survives the page unloading, so this
  // still reaches the server even when we're not the one waiting around for it to finish.
  try {
    const token = getAccessToken()
    await fetch(`${config.apiServerUrl}/users/logout`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      keepalive: true,
    })
  } catch (error) {
    console.log('Failed to clear active session before logout:', error)
  }
  return keycloak.logout()
}

export const forceLogin = () =>
  Promise.allSettled([logout, redirectToLogin].map((fn) => fn()))

export const getAccessToken = () => {
  return keycloak.token
}

// Available even when the bootstrap `/users` call itself failed (TENANT_INACTIVE /
// NO_TENANT_ACCESS) — the token is already decoded client-side by keycloak-js, no extra request.
export const getEmail = () => {
  return keycloak.tokenParsed?.email as string | undefined
}

export default keycloak